import {BASE_AUDIT_API, authorizedFetch, fetchAPI, getHttpErrorMessage} from '@/services/api';

const base = `${BASE_AUDIT_API}/fs-preview-download`;

/** Preview tab types only; AFS Excel is zip-only (no preview type). */
export type FsPreviewType = 'BS_PL' | 'TAX_COM' | 'TAX_RETURN';

export interface FileDownloadResult {
    blob: Blob;
    filename?: string;
    contentType?: string;
}

function tryParseFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) return undefined;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    const raw = match?.[1] || match?.[2];
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw.replace(/\+/g, '%20'));
    } catch {
        return raw.replace(/\+/g, ' ');
    }
}

async function fetchFile(path: string, options?: RequestInit): Promise<FileDownloadResult> {
    const res = await authorizedFetch(path, options);
    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }
    const contentType = res.headers.get('content-type') || undefined;
    if ((contentType || '').toLowerCase().includes('json')) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }
    const filename = tryParseFilenameFromContentDisposition(res.headers.get('content-disposition'));
    const blob = await res.blob();
    return {blob, filename, contentType};
}

/** Lazy preview for one Dialog tab. */
export async function previewFsReport(
    type: FsPreviewType,
    companyId: number,
    annualId: number,
    yearOfAssessment?: string,
) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        type,
    });
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchAPI<unknown>(`${base}/preview?${params.toString()}`);
}

/** Zip of BS&PL + TaxCom + TaxReturn + AFS (no OUTSTANDING). */
export async function downloadFsPreviewZip(
    companyId: number,
    annualId: number,
    yearOfAssessment?: string,
) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchFile(`${base}/download-zip?${params.toString()}`, {method: 'GET'});
}

export const auditFsPreviewDownloadService = {
    previewFsReport,
    downloadFsPreviewZip,
};
